/**
 * Mycelium PGMQ Health Monitor
 *
 * Replaces interval-based heartbeat polling with event-driven health tracking.
 * Instead of each service sending periodic HTTP/RPC heartbeats on a timer,
 * health status is derived from PGMQ event processing itself:
 *
 * 1. Services publish health_check events to PGMQ on meaningful state changes
 * 2. The mycelium-worker updates service_registry health from event processing
 * 3. Stale services are detected by the worker comparing last_heartbeat timestamps
 *
 * This eliminates:
 * - N×setInterval timers per service (replaced by event-driven updates)
 * - Wasted heartbeat RPCs when services are idle
 * - Timer drift and orphaned intervals after hot-reload
 *
 * @module mycelium-pgmq-health-monitor
 * @since 2026-03-23
 * @see docs/protocols/mycelium/WEBHOOK_ELIMINATION_PLAYBOOK.md
 */

import { getMyceliumTransport } from "./mycelium-transport-registry";
import type { IMyceliumTransport } from "./mycelium-transport.types";
import { IService } from "./service-registry";
import { logger } from "../lib/logger";

// ============================================================================
// TYPES
// ============================================================================

/** Health status levels */
export type HealthStatus = "healthy" | "degraded" | "unhealthy";

/** Health check event payload published to PGMQ */
export interface HealthCheckEvent {
  /** Service name reporting health */
  serviceName: string;
  /** Current health status */
  status: HealthStatus;
  /** Service type (connector, worker, enricher, etc.) */
  serviceType: string;
  /** Domains this service handles */
  domains: string[];
  /** Optional diagnostic details */
  details?: Record<string, unknown>;
  /** Timestamp of health check */
  checkedAt: string;
}

/** Configuration for the health monitor */
export interface HealthMonitorConfig {
  /** Optional custom transport (defaults to active transport) */
  transport?: IMyceliumTransport;
}

// ============================================================================
// HEALTH MONITOR
// ============================================================================

/**
 * PGMQ-native health monitor for Mycelium services.
 *
 * Instead of periodic heartbeats, services call `reportHealth()` on
 * meaningful state transitions (initialization, error recovery, degradation).
 * The mycelium-worker processes these events and updates the service registry.
 */
export class PgmqHealthMonitor implements IService {
  readonly name = "PgmqHealthMonitor";
  private transport: IMyceliumTransport | null;

  constructor(config?: HealthMonitorConfig) {
    this.transport = config?.transport ?? null;
  }

  async initialize(): Promise<void> {
    logger.info("❤️ PgmqHealthMonitor initialized");
  }

  async dispose(): Promise<void> {
    this.transport = null;
    logger.info("❤️ PgmqHealthMonitor disposed");
  }

  /**
   * Get the transport (lazy — uses active transport if none provided).
   */
  private getTransport(): IMyceliumTransport {
    return this.transport ?? getMyceliumTransport();
  }

  /**
   * Report a service's health status via PGMQ event.
   * Call this on meaningful state transitions, not on a timer.
   *
   * @example
   * ```ts
   * // On connector initialization
   * await healthMonitor.reportHealth({
   *   serviceName: 'activity-gamification',
   *   status: 'healthy',
   *   serviceType: 'connector',
   *   domains: ['task', 'sales'],
   * });
   *
   * // On error recovery
   * await healthMonitor.reportHealth({
   *   serviceName: 'xero-commission',
   *   status: 'degraded',
   *   serviceType: 'connector',
   *   domains: ['accounting'],
   *   details: { reason: 'xero_api_rate_limited', retryAfter: 60 },
   * });
   * ```
   */
  async reportHealth(event: Omit<HealthCheckEvent, "checkedAt">): Promise<{
    success: boolean;
    error?: string;
  }> {
    const healthEvent: HealthCheckEvent = {
      ...event,
      checkedAt: new Date().toISOString(),
    };

    try {
      const result = await this.getTransport().publish({
        domain: "system",
        type: "service_health_check",
        payload: {
          ...healthEvent,
          source: "PgmqHealthMonitor",
          priority:
            event.status === "unhealthy"
              ? "high"
              : event.status === "degraded"
                ? "high"
                : "normal",
        },
      });

      if (!result.success) {
        logger.warn("Health monitor: failed to publish health event", {
          service: event.serviceName,
          status: event.status,
          error: result.error,
        });
        return { success: false, error: result.error ? (typeof result.error === 'string' ? result.error : result.error.message) : 'Publish failed' };
      }

      logger.debug("Health monitor: health event published", {
        service: event.serviceName,
        status: event.status,
        messageId: result.data,
      });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logger.error("Health monitor: error publishing health event", {
        service: event.serviceName,
        error: message,
      });
      return { success: false, error: message };
    }
  }

  /**
   * Report multiple services' health in a single batch.
   * Useful during initialization or periodic batch health sweeps.
   */
  async reportBatchHealth(
    events: Array<Omit<HealthCheckEvent, "checkedAt">>
  ): Promise<{ total: number; succeeded: number; failed: number }> {
    const results = await Promise.allSettled(
      events.map((event) => this.reportHealth(event))
    );

    let succeeded = 0;
    let failed = 0;
    for (const result of results) {
      if (result.status === "fulfilled" && result.value.success) {
        succeeded++;
      } else {
        failed++;
      }
    }

    return { total: events.length, succeeded, failed };
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

export const pgmqHealthMonitor = new PgmqHealthMonitor();
